import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
} from 'antd';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import BreadCrumbs from '../../../../common/components/Breadcrumbs';
import { validateMessages } from '../../../../common/constants/common';
import { Page } from '../../../../common/constants/pageAction';
import {
  getObjectForUpdateMultiple,
  getSimpleDate,
  passDateToApi,
  forEditModal,
} from '../../../../common/helperFunction';
import { IInlineSearch } from '../../../../common/models/common';
import { ICmdbCiComputer } from '../../../../services/serviceNow/cmdbCiComputer/cmdbCiComputer.model';
import { ILookup } from '../../../../services/common/common.model';
import { getCmdbCiComputerById, saveCmdbCiComputer } from '../../../../store/serviceNow/cmdbCiComputer/cmdbCiComputer.action';
import {
  CmdbCiComputerSelector,
  clearCmdbCiComputerGetById,
  clearCmdbCiComputerMessages,
} from '../../../../store/serviceNow/cmdbCiComputer/cmdbCiComputer.reducer';
import { useAppSelector, useAppDispatch } from '../../../../store/app.hooks';
import {
  getBULookup,
  getCompanyLookup,
  getTenantLookup,
  updateMultiple,
} from '../../../../store/common/common.action';
import {
  clearBULookUp,
  clearCompanyLookUp,
  clearMultipleUpdateMessages,
  commonSelector,
} from '../../../../store/common/common.reducer';
import { globalSearchSelector } from '../../../../store/globalSearch/globalSearch.reducer';
import { IAddCmdbCiComputerProps } from './addCmdbCiComputer.model';

const { Option } = Select;

const AddCmdbCiComputerModal: React.FC<IAddCmdbCiComputerProps> = (props) => {
  const CmdbCiComputer = useAppSelector(CmdbCiComputerSelector);
  const commonLookups = useAppSelector(commonSelector);
  const dispatch = useAppDispatch();
  const globalFilters = useAppSelector(globalSearchSelector);

  const { id, showModal, handleModalClose, refreshDataTable, isMultiple, valuesForSelection } =
    props;

  const isNew: boolean = id || isMultiple ? false : true;
  const title = useMemo(() => {
    return (
      <>
        {isNew ? 'Add ' : 'Edit '} <BreadCrumbs pageName={Page.CmdbCiComputer} level={1} />
      </>
    );
  }, [isNew]);
  const submitButtonText = useMemo(() => {
    return isNew ? 'Save' : 'Update';
  }, [isNew]);

  const [form] = Form.useForm();

  let initialValues: ICmdbCiComputer = {
    tenant_id: null,
    company_id: null,
    bu_id: null,
    fqdn: '',
    name: '',
    manufacturer: '',
    os: '',
    sys_class_name: '',
    sys_created_on: '',
    sys_updated_on: '',
    discovery_source: '',
    sys_updated_by: '',
    sys_created_by: '',
    serial_number: '',
    cpu_count: null,
    cpu_core_count: null,
    processor: '',
    metric_type: '',
    virtual: false,
    used_for: '',
    hardware_status: '',
    u_decommission_date: '',
    sys_domain: '',
    dns_domain: '',
    os_domain: '',
    cpu_name: '',
    first_discovered: '',
    model_id: '',
    assigned_to: '',
    location: '',
    install_status: '',
    date_added: getSimpleDate(),
  };

  const onFinish = (values: any) => {
    const inputValues: ICmdbCiComputer = {
      ...values,
      id: id ? +id : null,
    };
    inputValues.date_added = passDateToApi(inputValues.date_added, true);
    if (!isMultiple) {
      dispatch(saveCmdbCiComputer(inputValues));
    } else {
      const result = getObjectForUpdateMultiple(
        valuesForSelection,
        inputValues,
        CmdbCiComputer.search.tableName
      );
      if (result) {
        dispatch(updateMultiple(result));
      }
    }
  };

  const handleTenantChange = (tenantId: number) => {
    form.setFieldsValue({ tenant_id: tenantId, company_id: null, bu_id: null });
    if (tenantId) {
      dispatch(getCompanyLookup(tenantId));
      dispatch(clearBULookUp());
    } else {
      dispatch(clearCompanyLookUp());
      dispatch(clearBULookUp());
    }
  };

  const handleCompanyChange = (companyId: number) => {
    form.setFieldsValue({ company_id: companyId, bu_id: null });
    if (companyId) {
      dispatch(getBULookup(companyId));
    } else {
      dispatch(clearBULookUp());
    }
  };

  const handleBUChange = (buId: number) => {
    form.setFieldsValue({ bu_id: buId });
  };

  const fillValuesOnEdit = async (data) => {
    if (data.tenant_id) {
      await dispatch(getCompanyLookup(data.tenant_id));
    }
    if (data.company_id) {
      await dispatch(getBULookup(data.company_id));
    }
    if (data) {
      initialValues = {
        tenant_id: _.isNull(data.tenant_id) ? null : data.tenant_id,
        company_id: _.isNull(data.company_id) ? null : data.company_id,
        bu_id: _.isNull(data.bu_id) ? null : data.bu_id,
        fqdn: data.fqdn,
        name: data.name,
        manufacturer: data.manufacturer,
        os: data.os,
        sys_class_name: data.sys_class_name,
        sys_created_on: data.sys_created_on,
        sys_updated_on: data.sys_updated_on,
        discovery_source: data.discovery_source,
        sys_updated_by: data.sys_updated_by,
        sys_created_by: data.sys_created_by,
        serial_number: data.serial_number,
        cpu_count: data.cpu_count,
        cpu_core_count: data.cpu_core_count,
        processor: data.processor,
        metric_type: data.metric_type,
        virtual: data.virtual,
        used_for: data.used_for,
        hardware_status: data.hardware_status,
        u_decommission_date: data.u_decommission_date,
        sys_domain: data.sys_domain,
        dns_domain: data.dns_domain,
        os_domain: data.os_domain,
        cpu_name: data.cpu_name,
        first_discovered: data.first_discovered,
        model_id: data.model_id,
        assigned_to: data.assigned_to,
        location: data.location,
        install_status: data.install_status,
        date_added: _.isNull(data.date_added) ? null : forEditModal(data.date_added),
      };
      form.setFieldsValue(initialValues);
    }
  };

  useEffect(() => {
    if (CmdbCiComputer.save.messages.length > 0) {
      if (CmdbCiComputer.save.hasErrors) {
        toast.error(CmdbCiComputer.save.messages.join(' '));
      } else {
        toast.success(CmdbCiComputer.save.messages.join(' '));
        handleModalClose();
        refreshDataTable();
      }
      dispatch(clearCmdbCiComputerMessages());
    }
  }, [CmdbCiComputer.save.messages]);

  useEffect(() => {
    if (commonLookups.save.messages.length > 0) {
      if (commonLookups.save.hasErrors) {
        toast.error(commonLookups.save.messages.join(' '));
      } else {
        toast.warn(commonLookups.save.messages.join(' '));
        handleModalClose();
      }
      dispatch(clearMultipleUpdateMessages());
    }
  }, [commonLookups.save.messages]);

  useEffect(() => {
    if (+id > 0 && CmdbCiComputer.getById.data) {
      const data = CmdbCiComputer.getById.data;
      fillValuesOnEdit(data);
    }
  }, [CmdbCiComputer.getById.data]);

  useEffect(() => {
    if (Object.keys(globalFilters?.globalTenantLookup?.data).length == 0) {
      dispatch(getTenantLookup());
    }
    if (+id > 0) {
      dispatch(getCmdbCiComputerById(+id));
    }
    return () => {
      dispatch(clearCmdbCiComputerGetById());
      dispatch(clearCompanyLookUp());
      dispatch(clearBULookUp());
    };
  }, [dispatch]);

  useEffect(() => {
    if (+id === 0 && !isMultiple) {
      const globalSearch: IInlineSearch = {};
      for (const key in globalFilters.search) {
        const element = globalFilters.search[key];
        globalSearch[key] = element ? [element] : null;
      }
      if (globalFilters.search.tenant_id && globalFilters.search.tenant_id !== 0) {
        if (!globalFilters.search.company_id) {
          dispatch(getCompanyLookup(globalSearch.tenant_id[0]));
        }
        if (!globalFilters.search.bu_id && globalFilters.search.company_id !== 0) {
          dispatch(getBULookup(globalSearch.company_id[0]));
        }
        const initlValues = {
          company_id: _.isNull(globalSearch.company_id) ? null : globalSearch.company_id[0],
          bu_id: _.isNull(globalSearch.bu_id) ? null : globalSearch.bu_id[0],
          tenant_id: _.isNull(globalSearch.tenant_id) ? null : globalSearch.tenant_id[0],
        };
        form.setFieldsValue(initlValues);
      }
    }
  }, []);

  return (
    <>
      <Modal
        wrapClassName="custom-modal"
        title={title}
        centered
        visible={showModal}
        onCancel={handleModalClose}
        footer={false}
      >
        {CmdbCiComputer.getById.loading ? (
          <div className="spin-loader">
            <Spin spinning={CmdbCiComputer.getById.loading} />
          </div>
        ) : (
          <Form
            form={form}
            name="addCmdbCiComputer"
            initialValues={initialValues}
            onFinish={onFinish}
            validateMessages={validateMessages}
          >
            <Row gutter={[30, 15]} className="form-label-hide">
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'tenant_id']} valuePropName="checked" noStyle>
                      <Checkbox>Tenant</Checkbox>
                    </Form.Item>
                  ) : (
                    'Tenant'
                  )}
                  <Form.Item
                    name="tenant_id"
                    className="m-0"
                    label="Tenant"
                    rules={[{ required: !isMultiple }]}
                  >
                    <Select
                      onChange={handleTenantChange}
                      loading={commonLookups.tenantLookup.loading}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA: any, optionB: any) =>
                        optionA.children
                          ?.toLowerCase()
                          ?.localeCompare(optionB.children?.toLowerCase())
                      }
                    >
                      {Object.keys(globalFilters?.globalTenantLookup?.data).length > 0
                        ? globalFilters?.globalTenantLookup?.data.map((option: ILookup) => (
                          <Option key={option.id} value={option.id}>
                            {option.name}
                          </Option>
                        ))
                        : commonLookups.tenantLookup.data.map((option: ILookup) => (
                          <Option key={option.id} value={option.id}>
                            {option.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'company_id']} valuePropName="checked" noStyle>
                      <Checkbox>Company</Checkbox>
                    </Form.Item>
                  ) : (
                    'Company'
                  )}
                  <Form.Item name="company_id" className="m-0" label="Company">
                    <Select
                      onChange={handleCompanyChange}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA: any, optionB: any) =>
                        optionA.children
                          ?.toLowerCase()
                          ?.localeCompare(optionB.children?.toLowerCase())
                      }
                      loading={commonLookups.companyLookup.loading}
                    >
                      {Object.keys(commonLookups.companyLookup.data).length > 0
                        ? commonLookups.companyLookup.data.map((option: ILookup) => (
                          <Option key={option.id} value={option.id}>
                            {option.name}
                          </Option>
                        ))
                        : globalFilters?.globalCompanyLookup?.data.map((option: ILookup) => (
                          <Option key={option.id} value={option.id}>
                            {option.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'bu_id']} valuePropName="checked" noStyle>
                      <Checkbox>BU</Checkbox>
                    </Form.Item>
                  ) : (
                    'BU'
                  )}
                  <Form.Item name="bu_id" className="m-0" label="BU">
                    <Select
                      onChange={handleBUChange}
                      loading={commonLookups.buLookup.loading}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA: any, optionB: any) =>
                        optionA.children
                          ?.toLowerCase()
                          ?.localeCompare(optionB.children?.toLowerCase())
                      }
                    >
                      {Object.keys(commonLookups.buLookup.data).length > 0
                        ? commonLookups.buLookup.data.map((option: ILookup) => (
                          <Option key={option.id} value={option.id}>
                            {option.name}
                          </Option>
                        ))
                        : globalFilters?.globalBULookup?.data.map((option: ILookup) => (
                          <Option key={option.id} value={option.id}>
                            {option.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item
                      name={['checked', 'fqdn']}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>FDQN</Checkbox>
                    </Form.Item>
                  ) : (
                    'FDQN'
                  )}
                  <Form.Item
                    name="fqdn"
                    label="FDQN"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'name']} valuePropName="checked" noStyle>
                      <Checkbox>Name</Checkbox>
                    </Form.Item>
                  ) : (
                    'Name'
                  )}
                  <Form.Item
                    name="name"
                    className="m-0"
                    label="Name"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'manufacturer']} valuePropName="checked" noStyle>
                      <Checkbox>Manufacturer</Checkbox>
                    </Form.Item>
                  ) : (
                    'Manufacturer'
                  )}
                  <Form.Item
                    name="manufacturer"
                    label="Manufacturer"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'os']} valuePropName="checked" noStyle>
                      <Checkbox>OS</Checkbox>
                    </Form.Item>
                  ) : (
                    'OS'
                  )}
                  <Form.Item
                    name="os"
                    label="OS"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'sys_class_name']} valuePropName="checked" noStyle>
                      <Checkbox>System Class Name</Checkbox>
                    </Form.Item>
                  ) : (
                    'System Class Name'
                  )}
                  <Form.Item
                    name="sys_class_name"
                    label="System Class Name"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item
                      name={['checked', 'sys_created_on']}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>System Created On</Checkbox>
                    </Form.Item>
                  ) : (
                    'System Created On'
                  )}
                  <Form.Item name="sys_created_on" label="System Created On" className="m-0">
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item
                      name={['checked', 'sys_updated_on']}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>System Updated On</Checkbox>
                    </Form.Item>
                  ) : (
                    'System Updated On'
                  )}
                  <Form.Item
                    name="sys_updated_on"
                    label="System Updated On"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'discovery_source']} valuePropName="checked" noStyle>
                      <Checkbox>Discovery Source</Checkbox>
                    </Form.Item>
                  ) : (
                    'Discovery Source'
                  )}
                  <Form.Item name="discovery_source" label="Discovery Source" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'sys_updated_by']} valuePropName="checked" noStyle>
                      <Checkbox>System Updated By</Checkbox>
                    </Form.Item>
                  ) : (
                    'System Updated By'
                  )}
                  <Form.Item
                    name="sys_updated_by"
                    label="System Updated By"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'sys_created_by']} valuePropName="checked" noStyle>
                      <Checkbox>System Created By</Checkbox>
                    </Form.Item>
                  ) : (
                    'System Created By'
                  )}
                  <Form.Item
                    name="sys_created_by"
                    label="System Created By"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'serial_number']} valuePropName="checked" noStyle>
                      <Checkbox>Serial Number</Checkbox>
                    </Form.Item>
                  ) : (
                    'Serial Number'
                  )}
                  <Form.Item name="serial_number" label="Serial Number" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item
                      name={['checked', 'processor']}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>Processor</Checkbox>
                    </Form.Item>
                  ) : (
                    'Processor'
                  )}
                  <Form.Item
                    name="processor"
                    label="Processor"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item
                      name={['checked', 'metric_type']}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>Metric Type</Checkbox>
                    </Form.Item>
                  ) : (
                    'Metric Type'
                  )}
                  <Form.Item name="metric_type" label="Metric Type" className="m-0">
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item
                      name={['checked', 'used_for']}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>Used For</Checkbox>
                    </Form.Item>
                  ) : (
                    'Used For'
                  )}
                  <Form.Item
                    name="used_for"
                    label="Used For"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'hardware_status']} valuePropName="checked" noStyle>
                      <Checkbox>Hardware Status</Checkbox>
                    </Form.Item>
                  ) : (
                    'Hardware Status'
                  )}
                  <Form.Item name="hardware_status" label="Hardware Status" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'date_added']} valuePropName="checked" noStyle>
                      <Checkbox>Date Added</Checkbox>
                    </Form.Item>
                  ) : (
                    'Date Added'
                  )}
                  <Form.Item name="date_added" label="Date Added" className="m-0">
                    <DatePicker className="form-control w-100" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item
                      name={['checked', 'u_decommission_date']}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>U Commission Date</Checkbox>
                    </Form.Item>
                  ) : (
                    'U Commission Date'
                  )}
                  <Form.Item
                    name="u_decommission_date"
                    label="U Commission Date"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'sys_domain']} valuePropName="checked" noStyle>
                      <Checkbox>System Domain</Checkbox>
                    </Form.Item>
                  ) : (
                    'System Domain'
                  )}
                  <Form.Item
                    name="sys_domain"
                    label="System Domain"
                    className="m-0"
                    rules={[{ max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'cpu_count']} valuePropName="checked" noStyle>
                      <Checkbox>CPU Count</Checkbox>
                    </Form.Item>
                  ) : (
                    'CPU Count'
                  )}
                  <Form.Item
                    name="cpu_count"
                    label="CPU Count"
                    className="m-0"
                    rules={[{ type: 'integer' }]}
                  >
                    <InputNumber min={1} className="form-control w-100" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'cpu_core_count']} valuePropName="checked" noStyle>
                      <Checkbox>CPU Core Count</Checkbox>
                    </Form.Item>
                  ) : (
                    'CPU Core Count'
                  )}
                  <Form.Item
                    name="cpu_core_count"
                    label="CPU Core Count"
                    className="m-0"
                    rules={[{ type: 'integer' }]}
                  >
                    <InputNumber min={1} className="form-control w-100" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'dns_domain']} valuePropName="checked" noStyle>
                      <Checkbox>DNS Domain</Checkbox>
                    </Form.Item>
                  ) : (
                    'DNS Domain'
                  )}
                  <Form.Item name="dns_domain" label="DNS Domain" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'os_domain']} valuePropName="checked" noStyle>
                      <Checkbox>OS Domain</Checkbox>
                    </Form.Item>
                  ) : (
                    'OS Domain'
                  )}
                  <Form.Item name="os_domain" label="OS Domain" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'cpu_name']} valuePropName="checked" noStyle>
                      <Checkbox>CPU Name</Checkbox>
                    </Form.Item>
                  ) : (
                    'CPU Name'
                  )}
                  <Form.Item name="cpu_name" label="CPU Name" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'first_discovered']} valuePropName="checked" noStyle>
                      <Checkbox>First Discovered</Checkbox>
                    </Form.Item>
                  ) : (
                    'First Discovered'
                  )}
                  <Form.Item name="first_discovered" label="First Discovered" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'model_id']} valuePropName="checked" noStyle>
                      <Checkbox>Model ID</Checkbox>
                    </Form.Item>
                  ) : (
                    'Model ID'
                  )}
                  <Form.Item name="model_id" label="Model ID" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'assigned_to']} valuePropName="checked" noStyle>
                      <Checkbox>Assigned To</Checkbox>
                    </Form.Item>
                  ) : (
                    'Assigned To'
                  )}
                  <Form.Item name="assigned_to" label="Assigned To" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'location']} valuePropName="checked" noStyle>
                      <Checkbox>Location</Checkbox>
                    </Form.Item>
                  ) : (
                    'Location'
                  )}
                  <Form.Item name="location" label="Location" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'install_status']} valuePropName="checked" noStyle>
                      <Checkbox>Install Status</Checkbox>
                    </Form.Item>
                  ) : (
                    'Install Status'
                  )}
                  <Form.Item name="install_status" label="Install Status" className="m-0" rules={[{ max: 255 }]}>
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
            </Row>
            <Row gutter={[30, 15]} className="form-label-hide">
              <Col xs={24} sm={12} md={8}>
                <div className="form-group form-inline-pt m-0">
                  <Form.Item name="virtual" className="m-0 mr-1" valuePropName="checked">
                    <Switch className="form-control" />
                  </Form.Item>
                  {isMultiple ? (
                    <Form.Item name={['checked', 'virtual']} valuePropName="checked" noStyle>
                      <Checkbox>Virtual</Checkbox>
                    </Form.Item>
                  ) : (
                    'Virtual'
                  )}
                </div>
              </Col>
            </Row>
            <div className="btns-block modal-footer">
              <Button
                key="submit"
                type="primary"
                htmlType="submit"
                loading={CmdbCiComputer.save.loading || commonLookups.save.loading}
              >
                {submitButtonText}
              </Button>
              <Button key="back" onClick={handleModalClose}>
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </>
  );
};
export default AddCmdbCiComputerModal;
